import React, { useCallback, useEffect, useRef, useState } from "react";

const draftListeners = new Set<(key: string, value: string) => void>();
const DRAFT_PREFIX = "clank:chat-draft:";

export const readChatDraft = (key: string): string => {
  try {
    return window.sessionStorage.getItem(`${DRAFT_PREFIX}${key}`) ?? "";
  } catch {
    return "";
  }
};

export const setChatDraft = (key: string, value: string) => {
  try {
    const storageKey = `${DRAFT_PREFIX}${key}`;
    if (value === "") window.sessionStorage.removeItem(storageKey);
    else window.sessionStorage.setItem(storageKey, value);
  } catch {
    // Storage may be unavailable in a locked-down browser. The in-memory state
    // still works for as long as the composer remains mounted.
  }
  for (const listener of draftListeners) listener(key, value);
};

/**
 * An unsent draft scoped to one chat. sessionStorage survives route changes
 * and reloads in this tab without retaining potentially sensitive text after
 * the tab is closed.
 */
export const useChatDraft = (
  key: string,
): [string, React.Dispatch<React.SetStateAction<string>>] => {
  const [draft, setDraftState] = useState(() => readChatDraft(key));

  const draftRef = useRef(draft);
  useEffect(() => {
    const update = (changedKey: string, value: string) => {
      if (changedKey !== key) return;
      draftRef.current = value;
      setDraftState(value);
    };
    update(key, readChatDraft(key));
    draftListeners.add(update);
    return () => {
      draftListeners.delete(update);
    };
  }, [key]);

  const setDraft = useCallback<React.Dispatch<React.SetStateAction<string>>>(
    next => {
      const value = typeof next === "function" ? next(draftRef.current) : next;
      draftRef.current = value;
      setChatDraft(key, value);
      setDraftState(value);
    },
    [key],
  );

  return [draft, setDraft];
};
